'use client';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { useTranslation } from '@/hooks/useTranslation';
import {
  useAdminToolFrequency,
  useAdminSessionStats,
  useAdminTurnGap,
  useAdminServiceTierMix,
  useAdminStopReasonMix,
  useAdminGitBranchHeatmap,
} from '../_lib/hooks';

const PIE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#ec4899', '#64748b'];

interface Props {
  period: string;
}

export function RichMetadataCharts({ period }: Props) {
  const { t } = useTranslation();
  const tt = (t.aiUsage as any).rich ?? defaultLabels;
  const toolFreq = useAdminToolFrequency(period).data?.items ?? [];
  const sessionStats = useAdminSessionStats({ period }).data ?? null;
  const turnGap = useAdminTurnGap(period).data ?? null;
  const tierMix = useAdminServiceTierMix(period).data?.items ?? [];
  const stopReason = useAdminStopReasonMix(period).data?.items ?? [];
  const branchHeat = useAdminGitBranchHeatmap(period).data?.items ?? [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
      {/* 工具频次 */}
      <ChartCard title={tt.toolFrequency}>
        {toolFreq.length === 0 ? (
          <EmptyHint />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={toolFreq.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="useCount" fill="#2563eb" name={tt.useCount} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Session 时长分布 */}
      <ChartCard
        title={tt.sessionDuration}
        subtitle={
          sessionStats
            ? `${tt.sessionCount}: ${sessionStats.sessionCount} · ${tt.avgDuration}: ${formatSec(sessionStats.avgDurationSec)} · ${tt.avgTurns}: ${sessionStats.avgTurns}`
            : undefined
        }
      >
        {sessionStats ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={bucketsToArray(sessionStats.durationBuckets)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="bucket" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#10b981" name={tt.sessionCount} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyHint />
        )}
      </ChartCard>

      {/* Turn 间隔分布 */}
      <ChartCard
        title={tt.turnGap}
        subtitle={
          turnGap
            ? `${tt.avgGap}: ${turnGap.avgGapSec.toFixed(1)}s · ${tt.medianGap}: ${turnGap.medianGapSec.toFixed(1)}s`
            : undefined
        }
      >
        {turnGap ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={bucketsToArray(turnGap.buckets)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="bucket" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#f59e0b" name={tt.turnGap} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyHint />
        )}
      </ChartCard>

      {/* Service Tier 配比 */}
      <ChartCard title={tt.serviceTier}>
        {tierMix.length === 0 ? (
          <EmptyHint />
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={tierMix.map((it) => ({ name: it.tier, value: Number(it.costUsd) }))}
                dataKey="value"
                nameKey="name"
                outerRadius={80}
                label={(e: any) => `${e.name}: $${Number(e.value).toFixed(2)}`}
              >
                {tierMix.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Stop Reason 配比 */}
      <ChartCard title={tt.stopReason}>
        {stopReason.length === 0 ? (
          <EmptyHint />
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stopReason}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="reason" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="eventCount" fill="#8b5cf6" name={tt.eventCount} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Git Branch 热力图 */}
      <ChartCard title={tt.gitBranchHeatmap}>
        {branchHeat.length === 0 ? (
          <EmptyHint />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={branchHeat.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="branch" width={160} tick={{ fontSize: 10 }} />
              <Tooltip
                formatter={(val: any, name: any, p: any) => {
                  if (name === 'tokens') return [Number(val).toLocaleString(), tt.tokens];
                  return [val, name];
                }}
              />
              <Bar dataKey="tokens" fill="#14b8a6" name={tt.tokens} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
}

function ChartCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded shadow p-4">
      <div className="mb-3">
        <div className="text-sm font-medium text-gray-700">{title}</div>
        {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function EmptyHint() {
  return <div className="h-[240px] flex items-center justify-center text-xs text-gray-400">—</div>;
}

function bucketsToArray(b: Record<string, number>): Array<{ bucket: string; count: number }> {
  return Object.entries(b).map(([bucket, count]) => ({ bucket, count }));
}

function formatSec(s: number): string {
  if (s >= 3600) return (s / 3600).toFixed(1) + 'h';
  if (s >= 60) return (s / 60).toFixed(0) + 'm';
  return s + 's';
}

// 兜底文案（locale 加载失败 / 未配置时回退英文）
const defaultLabels = {
  toolFrequency: 'Tool Frequency',
  useCount: 'Use Count',
  sessionDuration: 'Session Duration Distribution',
  sessionCount: 'Sessions',
  avgDuration: 'Avg Duration',
  avgTurns: 'Avg Turns',
  turnGap: 'Turn Gap Distribution',
  avgGap: 'Avg Gap',
  medianGap: 'Median',
  serviceTier: 'Service Tier (Cost Share)',
  stopReason: 'Stop Reason Distribution',
  eventCount: 'Events',
  gitBranchHeatmap: 'Top Branches by Tokens',
  tokens: 'Tokens',
};
